import React, { useState, useEffect } from 'react';
import LanguageSwitcher from '../LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';

function Header() {
  const { t } = useTranslation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Track scroll position to change header appearance
  useEffect(() => {
    if (isAdminRoute) return; // Don't add event listeners for admin routes
    
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);
  
  // Don't render the header on admin routes
  if (isAdminRoute) {
    return null;
  }

  // Check if a menu item is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className={`w-full fixed top-0 left-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-black shadow-md py-2' 
        : 'bg-black bg-opacity-90 py-3 sm:py-4'
    }`}>
      <div className="container max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Main navigation */}
        <nav className="flex justify-between items-center">
          {/* Logo section - left side */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group flex-shrink-0">
            <div className={`h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 rounded-full overflow-hidden border-2 ${scrolled ? 'border-white/30' : 'border-white/20'} transition-all duration-300 shadow-lg transform group-hover:scale-105`}>
              <img 
                src="/Logo.jpeg" 
                alt="Karigar Foundation Logo" 
                className="h-full w-full object-cover" 
              />
            </div>
            <div className="transition-all duration-300">
              <h1 className="font-heading font-bold text-sm sm:text-lg md:text-xl text-white">Karigar Foundation</h1>
              <p className="text-xs md:text-sm text-gray-300 hidden xs:block">मजदूरों की सेवा में</p>
            </div>
          </Link>

          {/* Desktop Menu - center with animation */}
          <ul className="hidden lg:flex items-center space-x-4">
            {[
              { path: '/', label: t('home') },
              { path: '/about', label: t('about') },
              { path: '/projects', label: t('projects') },
              { path: '/impact', label: t('impact') },
              { path: '/events', label: t('events.title', 'Events') },
              { path: '/contact', label: t('contact') }
            ].map((item, index) => (
              <li key={index} className="relative group">
                <Link 
                  to={item.path}
                  className={`px-3 py-2 font-medium text-sm uppercase tracking-wider transition-all duration-300 block 
                    ${isActive(item.path) 
                      ? 'text-white' 
                      : 'text-gray-300 hover:text-white'}`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-white transform origin-left scale-x-0 transition-transform duration-300 
                    ${isActive(item.path) ? 'scale-x-100' : 'group-hover:scale-x-100'}`}></span>
                </Link>
              </li>
            ))}
          </ul>
          
          {/* Donate button - always visible on desktop */}
          <div className="hidden lg:block">
            <Link 
              to="/donate" 
              className="px-5 py-2 bg-white hover:bg-gray-200 text-black font-medium text-sm uppercase tracking-wider rounded-full transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              {t('donate')}
            </Link>
          </div>

          {/* Right side - Auth buttons, Language switcher and mobile button */}
          <div className="flex items-center space-x-2">
            {/* Auth Buttons - Desktop */}
            <div className="hidden lg:flex items-center space-x-2">
              {isAuthenticated ? (
                <div className="relative">
                  <button 
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-2 text-white py-1 px-2 rounded-md hover:bg-white/10 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="hidden xl:inline-block">{user?.name || 'User'}</span>
                    <svg 
                      className={`w-4 h-4 transform transition-transform ${isProfileMenuOpen ? 'rotate-180' : 'rotate-0'}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Profile dropdown menu */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5">
                      {isAdmin && (
                        <Link 
                          to="/admin" 
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          Admin Dashboard
                        </Link>

                        
                      )}
                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Profile
                      </Link>
                      <Link
                        to="/my-registrations"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        My Registrations
                      </Link>
                      <Link
                        to="/my-certificates"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        My Certificates
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setIsProfileMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="text-white hover:text-gray-200 px-3 py-2 text-sm font-medium"
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
            
            {/* Language Switcher */}
            <div className="flex items-center">
              <LanguageSwitcher />
            </div>
            
            {/* Donate button - visible on mobile, hidden on smallest screens */}
            <div className="hidden sm:block lg:hidden">
              <Link 
                to="/donate" 
                className="px-3 py-1.5 bg-white hover:bg-gray-200 text-black font-medium text-xs uppercase tracking-wider rounded-full transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                {t('donate')}
              </Link>
            </div>

            {/* Mobile menu button with animation */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-1 sm:p-2 text-white focus:outline-none focus:ring-2 focus:ring-white rounded-md ml-1"
              aria-label="Menu"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              <div className="w-6 h-6 flex items-center justify-center relative">
                <span 
                  aria-hidden="true"
                  className={`absolute h-0.5 w-5 bg-current transform transition duration-300 ease-in-out ${
                    isMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-1.5'
                  }`}
                ></span>
                <span 
                  aria-hidden="true"
                  className={`absolute h-0.5 w-5 bg-current transform transition-opacity duration-300 ease-in-out ${
                    isMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                ></span>
                <span 
                  aria-hidden="true"
                  className={`absolute h-0.5 w-5 bg-current transform transition duration-300 ease-in-out ${
                    isMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-1.5'
                  }`}
                ></span>
              </div>
            </button>
          </div>
        </nav>

        {/* Mobile Menu - animated dropdown with improved transitions */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMenuOpen 
              ? 'max-h-[calc(100vh-4rem)] opacity-100 border-t border-gray-700 mt-3'
              : 'max-h-0 opacity-0 border-none'
          }`}
        >
          <div className="py-4 bg-black/95 backdrop-blur-md rounded-b-lg shadow-2xl">
            <nav className="space-y-1">
              {[
                { path: '/', label: t('home'), icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                { path: '/about', label: t('about'), icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
                { path: '/projects', label: t('projects'), icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
                { path: '/impact', label: t('impact'), icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                { path: '/events', label: t('events.title', 'Events'), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                { path: '/contact', label: t('contact'), icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex items-center pl-4 sm:pl-6 pr-4 py-3 ${
                    isActive(item.path)
                      ? 'bg-gray-800/30 border-l-4 border-white'
                      : 'border-l-4 border-transparent hover:bg-gray-800/20 hover:border-gray-400'
                  } transition-colors duration-200`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg 
                    className={`w-5 h-5 mr-3 ${isActive(item.path) ? 'text-white' : 'text-gray-400'}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                  <span className={`${isActive(item.path) ? 'text-white font-medium' : 'text-gray-300'}`}>
                    {item.label}
                  </span>
                </Link>
              ))}
              <div className="px-4 sm:px-6 pt-3">
                <Link
                  to="/donate"
                  className="flex items-center justify-center w-full py-3 px-4 rounded-lg bg-white text-black font-medium shadow-md hover:bg-gray-200 transition-all duration-200 transform hover:scale-[1.02]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg 
                    className="w-5 h-5 mr-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {t('donate')}
                </Link>
              </div>
            </nav>

            {/* Auth Actions for Mobile */}
            <div className="px-4 sm:px-6 pt-6 pb-3 border-t border-gray-700 mt-4">
              {isAuthenticated ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 text-lg">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user?.name || 'User'}</p>
                      <p className="text-gray-400 text-sm">{user?.email}</p>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center pl-2 pr-4 py-2 text-gray-300 hover:text-white transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Admin Dashboard
                    </Link>
                  )}
                  
                  <Link
                    to="/profile"
                    className="flex items-center pl-2 pr-4 py-2 text-gray-300 hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </Link>
                  
                  <Link
                    to="/my-registrations"
                    className="flex items-center pl-2 pr-4 py-2 text-gray-300 hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    My Registrations
                  </Link>

                  <Link
                    to="/my-certificates"
                    className="flex items-center pl-2 pr-4 py-2 text-gray-300 hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    My Certificates
                  </Link>

                  <button
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center w-full text-left pl-2 pr-4 py-2 text-gray-300 hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-3">
                  <Link
                    to="/login"
                    className="flex items-center justify-center w-full py-3 px-4 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 transition-all duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Login
                  </Link>
                  
                  <Link
                    to="/register"
                    className="flex items-center justify-center w-full py-3 px-4 rounded-lg bg-white text-black font-medium hover:bg-gray-200 transition-all duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Register
                  </Link>
                </div>
              )}
            </div>

            {/* Language switcher for mobile */}
            <div className="px-4 sm:px-6 pt-3 pb-2 border-t border-gray-700">
              <p className="text-gray-400 text-sm mb-3 font-medium">Language / भाषा:</p>
              <div className="flex space-x-2">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
